// --- React ---
import React, { useState, useEffect } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 공통 UI 컴포넌트 ---
import { ModalShell, FormStack, FormField, FormLabel } from '@/components/ui';
import { Button, Input } from '@/components/ui';
import { PhoneInput, normalizePhone } from '@/components/ui/common/PhoneInput';

// --- 스토어 ---
import { useNotificationStore } from '../../../store/notificationStore';
import { useAppStore } from '../../../store/loadingStore';

// --- API ---
import { useUpdateCompany } from '@/api/company/useCompany';

// --- 타입 ---
import type { Company } from '@packages/types/company';

// --- 유효성 검사 · 에러 파싱 ---
import { parseCompanyApiError } from '@/utils/errors';

// ==================== 타입 정의 ====================

interface CompanyInfoEditModalProps {
  isOpen: boolean;
  company: Company | null;
  onClose: () => void;
  onSave: () => void;
}

// ==================== 컴포넌트 ====================

const CompanyInfoEditModal: React.FC<CompanyInfoEditModalProps> = ({
  isOpen,
  company,
  onClose,
  onSave,
}) => {
  // ==================== 다국어 ====================
  const { t } = useTranslation();

  // ==================== 스토어 ====================
  const { showSuccess, showError } = useNotificationStore();
  const { showLoading, hideLoading } = useAppStore();

  // ==================== 상태 관리 ====================
  const [formData, setFormData] = useState({
    name: '',
    telephone: '',
    location: '',
    description: '',
  });
  const [telephoneValid, setTelephoneValid] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // ==================== API 훅 ====================
  const updateMutation = useUpdateCompany(company?.id || 0);

  // ==================== 이펙트 ====================

  /** modal 이 열릴 때 / company 가 바뀔 때마다 formData 를 강제 동기화 */
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      name: company?.name ?? '',
      telephone: normalizePhone(company?.telephone ?? ''),
      location: company?.location ?? '',
      description: company?.description ?? '',
    });
    setTelephoneValid(true);
    setErrors({});
  }, [isOpen, company]);

  // ==================== 유효성 검사 ====================

  /** 필수 필드(회사명) 유효성 검사 후 에러 객체 반환 */
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      newErrors.name = i18nKeys.company.company_name_required;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== 핸들러 ====================

  /**
   * 저장 핸들러
   * 1. 유효성 검사 → 2. API 호출(회사 정보 수정) → 3. 성공/실패 알림
   */
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      showLoading();
      await updateMutation.mutateAsync({
        name: formData.name.trim(),
        telephone: formData.telephone.trim(),
        location: formData.location.trim(),
        description: formData.description.trim(),
      });
      showSuccess('company:company_update_success');
      onSave();
      onClose();
    } catch (error: unknown) {
      const e = parseCompanyApiError(error);
      showError(e.title, e.message);
    } finally {
      hideLoading();
    }
  };

  /** 모달 닫기 — 폼 데이터 초기화 후 부모 onClose 호출 */
  const handleClose = () => {
    setFormData({ name: '', telephone: '', location: '', description: '' });
    setTelephoneValid(true);
    setErrors({});
    onClose();
  };

  // ==================== 렌더링 ====================

  return (
    <ModalShell
      isOpen={isOpen}
      title={t(i18nKeys.company.company_info)}
      onClose={handleClose}
      size="md"
      footer={
        <>
          {/* 취소 버튼 */}
          <Button variant="secondary" onClick={handleClose} disabled={updateMutation.isPending}>
            {t(i18nKeys.company.cancel)}
          </Button>
          {/* 저장 버튼 */}
          <Button
            onClick={handleSave}
            disabled={
              updateMutation.isPending ||
              !formData.name.trim() ||
              (!!formData.telephone && !telephoneValid)
            }
          >
            {updateMutation.isPending ? t(i18nKeys.company.processing) : t(i18nKeys.company.save)}
          </Button>
        </>
      }
    >
      <FormStack>
        {/* 회사명 (필수) */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.company_name)} *</FormLabel>
          <Input
            value={formData.name}
            onChange={value => {
              setFormData({ ...formData, name: value });
              if (errors.name) setErrors({ ...errors, name: '' });
            }}
            placeholder={t(i18nKeys.company.company_name_placeholder)}
            error={errors.name ? t(errors.name) : undefined}
            size="md"
            fullWidth
          />
        </FormField>

        {/* 전화번호 */}
        <FormField>
          <PhoneInput
            label={t(i18nKeys.company.company_telephone)}
            value={formData.telephone}
            onChange={(value, isValid) => {
              setFormData({ ...formData, telephone: value });
              setTelephoneValid(isValid);
            }}
            fullWidth
          />
        </FormField>

        {/* 위치 */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.company_location)}</FormLabel>
          <Input
            value={formData.location}
            onChange={value => setFormData({ ...formData, location: value })}
            placeholder={t(i18nKeys.company.company_location_placeholder)}
            size="md"
            fullWidth
          />
        </FormField>

        {/* 설명 */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.company_description)}</FormLabel>
          <Input
            value={formData.description}
            onChange={value => setFormData({ ...formData, description: value })}
            placeholder={t(i18nKeys.company.company_description_placeholder)}
            multiline
            rows={3}
            size="md"
            fullWidth
          />
        </FormField>
      </FormStack>
    </ModalShell>
  );
};

export default CompanyInfoEditModal;
